import { marketingFonts as F, visualTokens as V } from '../../../theme_landing_page_2';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { MillosMark } from '../icons';

const INPUT_SPACE = 340;
const FLOW_WIDTH = 1260;
const CANVAS_WIDTH = FLOW_WIDTH + INPUT_SPACE;
const CANVAS_HEIGHT = 520;
const FLOW_SCALE = 0.9;
const HORIZONTAL_SPACING = 0.89;
const NODE_CENTERS: Record<string, number> = {
  millos: -164, provider: 182, tenant: 182, match: 528,
  jake: 900, you: 900, sarah: 900, visit: 1146,
};
const RETURN_PATH = 'M1182 182 L1230 182 Q1260 182 1260 212 L1260 370 Q1260 400 1230 400 L-280 400 Q-310 400 -310 370 L-310 216 Q-310 186 -280 186 L-164 186';

const CYCLE_SECONDS = 39;
const percent = (seconds: number) => `${seconds / CYCLE_SECONDS * 100}%`;
const stages = [
  { node: 'millos', glow: '.ml-input-mark', message: '.ml-detection-message', arrival: 3, end: 6 },
  { node: 'provider', glow: '', message: '', arrival: 7, end: 10 },
  { node: 'tenant', glow: '', message: '', arrival: 7, end: 10 },
  { node: 'match', glow: '> :first-child', message: '.ml-match-message', arrival: 11, end: 14 },
  { node: 'jake', glow: '> :first-child', message: '> :last-child', arrival: 15, end: 18 },
  { node: 'you', glow: '> :first-child', message: '> :last-child', arrival: 15, end: 18 },
  { node: 'sarah', glow: '> :first-child', message: '> :last-child', arrival: 15, end: 18 },
  { node: 'visit', glow: '> :first-child', message: '> :last-child', arrival: 19, end: 22 },
];
const reveal = (name: string, time: number) =>
  `@keyframes ${name}{0%{visibility:hidden}${percent(time)},100%{visibility:visible}}`;
const wireArrivals = [7, 7, 11, 11, 15, 15, 15, 19, 19, 19];
const EMAIL_WIRES = [0, 1, 4, 5, 6];
const wireAnimation = (name: string, arrival: number) => `
@keyframes ${name}{0%,${percent(arrival - 1)}{opacity:0;stroke-dashoffset:100}${percent(arrival)},100%{opacity:1;stroke-dashoffset:0}}
`;
const KEYFRAMES = `
@keyframes mlCertificate{0%{opacity:0;transform:translateX(0) scale(1)}${percent(.5)},${percent(1)}{opacity:1;transform:translateX(0) scale(1)}${percent(2.5)}{opacity:1;transform:translateX(100px) scale(.55)}${percent(3)},100%{opacity:0;transform:translateX(134px) scale(.05)}}
[data-millos-flow] .ml-certificate{animation:mlCertificate ${CYCLE_SECONDS}s ease-in-out infinite!important}
[data-millos-flow] .ml-ambient{display:none}
${Object.entries(NODE_CENTERS).map(([node, center]) =>
  `[data-millos-flow] [data-flow-node="${node}"]{translate:${center * (HORIZONTAL_SPACING - 1)}px 0}`
).join('')}
[data-millos-flow] .ml-certificate{margin-left:${-164 * (HORIZONTAL_SPACING - 1)}px}
${stages.map((stage, index) => {
  const { arrival, end } = stage;
  const start = arrival + (['jake', 'you', 'sarah'].includes(stage.node) ? 0 : 1);
  const selector = `[data-millos-flow] [data-flow-node="${stage.node}"]`;
  const pulseFrames = Array.from({ length: end - start }, (_, beat) =>
    `${percent(start + beat + .5)}{box-shadow:0 0 20px 8px ${V.brandVioletAlpha60}}${percent(start + beat + 1 - .001)}{box-shadow:0 0 10px 4px ${V.brandVioletAlpha35}}`
  ).join('');
  return `
@keyframes mlStageGlow${index}{0%,${percent(start - .001)},${percent(end)},100%{box-shadow:none}${percent(start)}{box-shadow:0 0 10px 4px ${V.brandVioletAlpha35}}${pulseFrames}${index === 0 ? `${percent(28.999)}{box-shadow:none}${percent(29)}{box-shadow:0 0 10px 4px ${V.brandVioletAlpha35}}${percent(30)},${percent(32)},${percent(34)}{box-shadow:0 0 20px 8px ${V.brandVioletAlpha60}}${percent(31)},${percent(33)}{box-shadow:0 0 10px 4px ${V.brandVioletAlpha35}}` : ''}}
${reveal(`mlStageMessage${index}`, arrival + (['jake', 'you', 'sarah'].includes(stage.node) ? 0 : 1))}
@keyframes mlStageNode${index}{0%{opacity:0}${percent(arrival)},100%{opacity:1}}
${index > 0 ? `${selector}{animation:mlStageNode${index} ${CYCLE_SECONDS}s steps(1,end) infinite!important}` : ''}
${selector} ${stage.glow}{animation:mlStageGlow${index} ${CYCLE_SECONDS}s linear infinite${stage.glow ? '' : `,mlStageNode${index} ${CYCLE_SECONDS}s steps(1,end) infinite`}!important}
${stage.message ? `${selector} ${stage.message}{animation:mlStageMessage${index} ${CYCLE_SECONDS}s steps(1,end) infinite!important}` : ''}
`;
}).join('')}
@keyframes mlPlanMessage{0%{visibility:hidden}${percent(5)}{visibility:visible}${percent(29)},100%{visibility:hidden}}
[data-millos-flow] .ml-plan-message{animation:mlPlanMessage ${CYCLE_SECONDS}s steps(1,end) infinite}
${reveal('mlConfirmingMessage', 13)}
[data-millos-flow] .ml-confirming-message{animation:mlConfirmingMessage ${CYCLE_SECONDS}s steps(1,end) infinite}
${[5, 13].map((time, index) => `
@keyframes mlExpandRow${index}{0%,${percent(time)}{max-height:0}${percent(time + .3)},100%{max-height:40px}}
[data-millos-flow] .ml-expand-row-${index}{overflow:hidden;animation:mlExpandRow${index} ${CYCLE_SECONDS}s ease-out infinite}
`).join('')}
${wireArrivals.map((arrival, i) => `
${wireAnimation(`mlConnection${i}`, arrival)}
[data-millos-flow] [data-flow-wire="${i}"]{stroke-dasharray:100;animation:mlConnection${i} ${CYCLE_SECONDS}s linear infinite!important}
`).join('')}
${EMAIL_WIRES.map((index) => {
  const arrival = wireArrivals[index];
  return `
@keyframes mlEmail${index}{0%,${percent(arrival - 1)}{opacity:0;offset-distance:0%}${percent(arrival - .999)}{opacity:1}${percent(arrival)}{opacity:1;offset-distance:100%}${percent(arrival + .001)},100%{opacity:0;offset-distance:100%}}
[data-millos-flow] [data-email-wire="${index}"]{animation:mlEmail${index} ${CYCLE_SECONDS}s linear infinite}
`;
}).join('')}
@keyframes mlCalendarDay{0%{background:${V.paperAlpha4};border-color:${V.paperAlpha8};box-shadow:none}${percent(8)},100%{background:var(--day-bg);border-color:var(--day-border);box-shadow:var(--day-glow)}}
${reveal('mlCalendarTick', 8)}
[data-millos-flow] .ml-calendar-day{animation:mlCalendarDay ${CYCLE_SECONDS}s steps(1,end) infinite}
[data-millos-flow] .ml-calendar-tick{animation:mlCalendarTick ${CYCLE_SECONDS}s steps(1,end) infinite}
@keyframes mlReturnLine{0%,${percent(22)}{opacity:0;stroke-dashoffset:100}${percent(22.001)}{opacity:1}${percent(28)},100%{opacity:1;stroke-dashoffset:0}}
[data-millos-flow] [data-return-line]{animation:mlReturnLine ${CYCLE_SECONDS}s linear infinite}
@keyframes mlReturnCertificate{0%,${percent(22)}{opacity:0;offset-distance:0%}${percent(22.001)},${percent(28)}{opacity:1}${percent(28)}{offset-distance:100%}${percent(28.001)},100%{opacity:0;offset-distance:100%}}
[data-millos-flow] .ml-return-certificate{offset-path:path('${RETURN_PATH}');offset-rotate:0deg;offset-anchor:center;animation:mlReturnCertificate ${CYCLE_SECONDS}s linear infinite}
@keyframes mlValidMessage{0%{visibility:hidden}100%{visibility:visible}}
@keyframes mlExpiryMessage{0%{visibility:hidden}${percent(4)}{visibility:visible}${percent(29)},100%{visibility:hidden}}
[data-millos-flow] [data-flow-node="millos"] .ml-detection-message{animation:mlExpiryMessage ${CYCLE_SECONDS}s steps(1,end) infinite!important}
[data-millos-flow] .ml-valid-message{animation:mlValidMessage 29s steps(1,end) forwards}
[data-millos-flow][data-started="false"] *{animation-play-state:paused!important}
[data-millos-flow][data-paused="true"] [data-flow-canvas] *{animation-play-state:paused!important}
[data-millos-flow][data-complete="true"][data-started] [data-flow-canvas] *{animation:none!important;visibility:visible!important;opacity:1;stroke-dashoffset:0;box-shadow:none!important}
[data-millos-flow][data-complete="true"] .ml-certificate,
[data-millos-flow][data-complete="true"] .ml-return-certificate,
[data-millos-flow][data-complete="true"] [data-email-wire],
[data-millos-flow][data-complete="true"] .ml-plan-message,
[data-millos-flow][data-complete="true"] .ml-detection-message{display:none}
@media (prefers-reduced-motion: reduce){
  [data-millos-flow] *{animation:none!important;visibility:visible!important;opacity:1;stroke-dashoffset:0}
  [data-millos-flow] .ml-detection-message,[data-millos-flow] .ml-plan-message{display:none}
  [data-millos-flow] .ml-certificate,[data-millos-flow] .ml-return-certificate{display:none}
  [data-millos-flow] [data-email-wire]{display:none}
}
`;

const WIRES = [
  { d: 'M-132 170 C -60 145, 0 100, 72 100', color: V.brandCyan }, // MillOS -> Provider
  { d: 'M-132 202 C -60 225, 0 290, 72 290', color: V.brandCyan }, // MillOS -> Tenant
  { d: 'M292 100 C 380 100, 438 150, 498 168', color: V.brandTeal }, // Provider -> Match
  { d: 'M292 290 C 380 290, 438 220, 498 200', color: V.brandTeal }, // Tenant -> Match
  { d: 'M564 182 C700 182 750 62 869.4 62', color: V.brandCyan },
  { d: 'M564 182 L869.4 182', color: V.brandCyan },
  { d: 'M564 182 C700 182 750 302 869.4 302', color: V.brandCyan },
  { d: 'M930.6 62 C1020 62 1040 150 1110 166', color: V.brandViolet },
  { d: 'M930.6 182 L1110 182', color: V.brandViolet },
  { d: 'M930.6 302 C1020 302 1040 214 1110 198', color: V.brandViolet },
];

const cardSx = {
  background: V.glassDarkAlpha90,
  backdropFilter: V.backdropFilterblur_14px,
  border: `1px solid ${V.paperAlpha14}`,
  borderRadius: V.borderRadius18px,
  boxShadow: V.flowCardShadow,
};

function calDays(open: number[], match: number): { letter: string; mark: string; bg: string; border: string; color: string; glow: string }[] {
  const L = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return L.map((letter, i) =>
    i === match
      ? { letter, mark: '✓', bg: V.brandTeal, border: V.brandTeal, color: V.ink, glow: `0 0 12px ${V.brandTealAlpha65}` }
      : open.includes(i)
      ? { letter, mark: '✓', bg: V.brandCyanAlpha18, border: V.brandCyanAlpha60, color: V.onDark, glow: 'none' }
      : { letter, mark: '', bg: V.paperAlpha4, border: V.paperAlpha8, color: V.onDarkMuted, glow: 'none' }
  );
}

const parties = [
  { role: 'Provider', name: 'Gas engineer', top: 20, days: calDays([3, 5], 5) },
  { role: 'Tenant', name: 'Tenant · Flat 2', top: 210, days: calDays([4, 5], 5) },
];

function GasCertificate() {
  return (
    <svg width="56" height="72" viewBox="0 0 56 72" fill="none" aria-hidden="true" style={{ filter: V.certificateShadow }}>
      <path d="M5 1h32l18 18v47a5 5 0 0 1-5 5H5a4 4 0 0 1-4-4V5a4 4 0 0 1 4-4Z" fill={V.paper} stroke={V.violetLight} strokeWidth="2" />
      <path d="M37 1v18h18" fill={V.fold} stroke={V.violetLight} strokeWidth="2" strokeLinejoin="round" />
      <text x="10" y="32" fill={V.ink} fontFamily={F.body} fontWeight={V.fontWeight700} fontSize={V.fontSize10}>GAS</text>
      <path d="M10 41h34M10 47h28M10 53h20" stroke={V.onDarkMuted} strokeWidth="2" strokeLinecap="round" />
      <circle cx="41" cy="59" r="7" fill={V.brandTeal} />
      <path d="m38 59 2 2 4-4" stroke={V.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const iconSx = {
  width: 72, height: 72, margin: '0 auto', borderRadius: '50%', background: V.paper,
  border: `2px solid ${V.brandViolet}`, display: 'grid', placeItems: 'center',
};
const messageSx = {
  ...cardSx, mt: '12px', padding: '8px 12px', fontFamily: F.mono,
  fontWeight: V.fontWeight500, fontSize: V.fontSize12, color: V.violetLight,
};

const MillosLoop: React.FC = () => {
  const maskId = useId().replace(/:/g, '');
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(FLOW_SCALE);
  const [width, setWidth] = useState(CANVAS_WIDTH);
  const [started, setStarted] = useState(false);
  const [complete, setComplete] = useState(false);
  const [paused, setPaused] = useState(false);
  const [replay, setReplay] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true);
          io.disconnect();
        }
      },
      // Narrow screens show a scrollable slice of the wide diagram.
      { threshold: 0.1 }
    );
    if (rootRef.current) io.observe(rootRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const fit = () => {
      const el = wrapRef.current;
      if (!el) return;
      const { width: w, height: h } = el.getBoundingClientRect();
      if (w > 0 && h > 0) {
        setScale(FLOW_SCALE * Math.min(1.2, w / CANVAS_WIDTH * 1.2, Math.max(0, h - 48) / 420));
        setWidth(w);
      }
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    return () => ro?.disconnect();
  }, []);

  const boxL = Math.round((width - CANVAS_WIDTH * HORIZONTAL_SPACING * scale) / 2);

  return (
    <Box ref={rootRef} data-millos-flow data-started={started} data-complete={complete} data-paused={paused} role="group" aria-label="MillOS certificate renewal flow" sx={{ position: 'relative', height: '100%', overflow: 'visible', padding: { xs: '4px 0 8px', md: '4px 12px 8px' } }}>
      <style>{KEYFRAMES}</style>
      {complete && (
        <Button
          variant="contained"
          onClick={() => { setStarted(true); setPaused(false); setComplete(false); setReplay((value) => value + 1); }}
          sx={{ position: 'absolute', bottom: 420 * scale + 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1, borderRadius: V.borderRadius24px, textTransform: 'none', backgroundColor: V.replayBackground, color: V.buttonText, '&:hover': { backgroundColor: V.replayHover } }}
        >
          Play Flow again
        </Button>
      )}
      <Box ref={wrapRef} sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <Box
          key={replay}
          data-flow-canvas
          role={complete ? undefined : 'button'}
          tabIndex={complete ? undefined : 0}
          aria-label={complete ? undefined : paused ? 'Resume flow' : 'Pause flow'}
          title={complete ? undefined : paused ? 'Click to resume' : 'Click to pause'}
          onClick={() => { if (!complete) setPaused((value) => !value); }}
          onKeyDown={(event) => {
            if (!complete && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              setPaused((value) => !value);
            }
          }}
          sx={{ position: 'absolute', left: boxL, bottom: -100 * scale, width: CANVAS_WIDTH * HORIZONTAL_SPACING, height: CANVAS_HEIGHT, transformOrigin: '0 100%', transform: `scale(${scale})`, cursor: complete ? 'default' : 'pointer', '&:focus-visible': { outline: `2px solid ${V.brandTeal}`, outlineOffset: 4 } }}
        >
          <svg aria-hidden="true" viewBox={`${-INPUT_SPACE * HORIZONTAL_SPACING} 0 ${CANVAS_WIDTH * HORIZONTAL_SPACING} ${CANVAS_HEIGHT}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }} fill="none">
            <defs>
              {WIRES.map((w, i) => (
                <mask key={i} id={`${maskId}-wire-${i}`} maskUnits="userSpaceOnUse" x={-INPUT_SPACE} y={0} width={CANVAS_WIDTH + 40} height={CANVAS_HEIGHT}>
                  <path data-flow-wire={i} d={w.d} pathLength={100} stroke="white" strokeWidth={6} />
                </mask>
              ))}
              <mask id={`${maskId}-return`} maskUnits="userSpaceOnUse" x={-INPUT_SPACE} y={0} width={CANVAS_WIDTH + 40} height={CANVAS_HEIGHT}>
                <path data-return-line d={RETURN_PATH} pathLength={100} stroke="white" strokeWidth={6} strokeDasharray={100} />
              </mask>
            </defs>
            <g transform={`scale(${HORIZONTAL_SPACING} 1)`}>
            {WIRES.map((w, i) => (
              <g key={i}>
                <path data-flow-connector d={w.d} stroke={V.brandCyan} strokeWidth={2} strokeLinecap="round" strokeDasharray="1 7" mask={`url(#${maskId}-wire-${i})`} />
              </g>
            ))}
            <path data-flow-connector d={RETURN_PATH} stroke={V.brandCyan} strokeWidth={2} strokeLinecap="round" strokeDasharray="1 7" mask={`url(#${maskId}-return)`} />
            </g>
          </svg>

          <Box sx={{ position: 'relative', left: INPUT_SPACE * HORIZONTAL_SPACING, width: FLOW_WIDTH, height: CANVAS_HEIGHT }}>
            <Box className="ml-certificate" role="img" aria-label="Gas certificate entering MillOS" sx={{ position: 'absolute', left: -326, top: 150, width: 56, height: 72, transformOrigin: 'center' }}>
              <GasCertificate />
            </Box>
            <Box sx={{ position: 'absolute', left: 0, top: 0, transform: `scaleX(${HORIZONTAL_SPACING})`, transformOrigin: '0 0' }}>
              {EMAIL_WIRES.map((index) => (
                <Box key={index} data-email-wire={index} role="img" aria-label="Email sent by MillOS" sx={{ position: 'absolute', left: 0, top: 0, width: 32, height: 24, offsetPath: `path('${WIRES[index].d}')`, offsetRotate: '0deg', offsetAnchor: 'center' }}>
                  <svg width="32" height="24" viewBox="0 0 32 24" fill="none" aria-hidden="true" style={{ transform: `scaleX(${1 / HORIZONTAL_SPACING})`, filter: V.emailShadow }}>
                    <rect x="1" y="1" width="30" height="22" rx="4" fill={V.paper} stroke={V.brandViolet} strokeWidth="2" />
                    <path d="m3 4 13 10L29 4M3 21l9-8m17 8-9-8" stroke={V.brandViolet} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Box>
              ))}
              <Box className="ml-return-certificate" role="img" aria-label="New gas certificate returning to MillOS" sx={{ position: 'absolute', left: 0, top: 0, width: 56, height: 72, '& > svg': { transform: `scaleX(${1 / HORIZONTAL_SPACING})` } }}>
                <GasCertificate />
              </Box>
            </Box>
            {/* 01 · MillOS detects expiry */}
            <Box data-flow-node="millos" sx={{ position: 'absolute', left: -264, top: 150, width: 200, textAlign: 'center' }}>
              <Box sx={{ position: 'relative', width: 72, height: 72, margin: '0 auto', display: 'grid', placeItems: 'center' }}>
                <Box className="ml-input-mark" sx={{ position: 'relative', width: 72, height: 72, borderRadius: '50%', background: V.paper, border: `2px solid ${V.brandViolet}`, display: 'grid', placeItems: 'center' }}>
                  <MillosMark size={36} />
                </Box>
              </Box>
              <Box className="ml-detection-message" sx={{ display: 'inline-block', mt: '12px', ...cardSx, padding: '8px 12px' }}>
                <Box sx={{ fontFamily: F.mono, fontWeight: V.fontWeight500, fontSize: V.fontSize12, color: V.violetLight }}>Certificate is expiring</Box>
                <Box className="ml-expand-row-0">
                  <Box className="ml-plan-message" sx={{ fontFamily: F.mono, fontWeight: V.fontWeight500, fontSize: V.fontSize12, color: V.violetLight, pt: '6px' }}>Plan renewal</Box>
                </Box>
              </Box>
              <Box className="ml-valid-message" onAnimationEnd={() => setComplete(true)} sx={{ ...messageSx, position: 'absolute', top: 72, left: 0, width: '100%', color: V.mintLight }}>
                Certificate is valid for 1 year. I will start a new flow then.
              </Box>
            </Box>

            {/* 02 · provider + tenant */}
            {parties.map((p) => (
              <Box key={p.role} data-flow-node={p.role.toLowerCase()} sx={{ position: 'absolute', left: 72, top: p.top, width: 220, ...cardSx, padding: '15px 14px 14px', textAlign: 'center' }}>
                <Box sx={{ position: 'absolute', left: 24, right: 24, bottom: -1, height: 2, borderRadius: V.borderRadius2px, background: `linear-gradient(90deg,transparent,${V.brandCyan},transparent)` }} />
                <Box sx={{ fontFamily: F.statLabel, fontWeight: V.fontWeight700, fontSize: V.fontSize16, letterSpacing: V.letterSpacing_0_02em, color: V.onDark, mt: '3px' }}>{p.name}</Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px', mt: '11px' }}>
                  {p.days.map((d, i) => (
                    <Box key={i} sx={{ textAlign: 'center' }}>
                      <Box sx={{ fontFamily: F.mono, fontSize: V.fontSize12, fontWeight: V.fontWeight500, color: V.onDarkMuted, mb: '3px' }}>{d.letter}</Box>
                      <Box className="ml-calendar-day" sx={{ '--day-bg': d.bg, '--day-border': d.border, '--day-glow': d.glow, height: 22, borderRadius: V.borderRadius6px, background: d.bg, border: `1px solid ${d.border}`, boxShadow: d.glow, display: 'grid', placeItems: 'center', fontSize: V.fontSize13, fontWeight: V.fontWeight700, color: d.color }}>
                        <span className="ml-calendar-tick">{d.mark}</span>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {/* 03 · match */}
            <Box data-flow-node="match" sx={{ position: 'absolute', left: 458, top: 146, width: 140, textAlign: 'center' }}>
              <Box sx={iconSx}>
                <MillosMark size={36} />
              </Box>
              <Box className="ml-match-message" sx={{ position: 'relative', left: 8, display: 'inline-block', mt: '12px', ...cardSx, borderRadius: V.borderRadius14px, padding: '10px 11px 11px' }}>
                <Box sx={{ fontFamily: F.mono, fontWeight: V.fontWeight600, fontSize: V.fontSize12, letterSpacing: V.letterSpacing0_08em, textTransform: 'uppercase', color: V.mintLight, whiteSpace: 'nowrap' }}>Match found</Box>
                <Box className="ml-expand-row-1">
                  <Box className="ml-confirming-message" sx={{ fontFamily: F.mono, fontWeight: V.fontWeight500, fontSize: V.fontSize12, color: V.violetLight, pt: '6px' }}>Confirming visit</Box>
                </Box>
              </Box>
            </Box>

            {[
              { id: 'jake', name: 'Provider', top: 26 },
              { id: 'you', name: 'You', top: 146 },
              { id: 'sarah', name: 'Tenant', top: 266 },
            ].map((person) => (
              <Box key={person.id} data-flow-node={person.id} sx={{ position: 'absolute', left: 850, top: person.top + 5.4, width: 100, textAlign: 'center' }}>
                <Box sx={{ ...iconSx, width: 61.2, height: 61.2 }}>
                  <svg width="27.2" height="27.2" viewBox="0 0 24 24" fill="none" stroke={V.brandViolet} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    {person.id === 'jake' ? (
                      <path d="M14.5 6.5a5 5 0 0 0-6.7 6.7l-5.1 5.1a2.1 2.1 0 0 0 3 3l5.1-5.1a5 5 0 0 0 6.7-6.7L14 13l-3-3 3.5-3.5Z" />
                    ) : person.id === 'you' ? (
                      <><circle cx="12" cy="8" r="4" /><path d="M4 22v-3a8 8 0 0 1 16 0v3" /></>
                    ) : (
                      <><path d="m3 10 9-7 9 7v11H3V10Z" /><path d="M9 21v-8h6v8" /></>
                    )}
                  </svg>
                </Box>
                <Box sx={{ ...messageSx, mt: '2px' }}>{person.name}</Box>
              </Box>
            ))}

            <Box data-flow-node="visit" sx={{ position: 'absolute', left: 1076, top: 146, width: 140, textAlign: 'center' }}>
              <Box sx={iconSx}>
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={V.brandViolet} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4M17 3v4M3 11h18m-13 5 3 3 5-5" />
                </svg>
              </Box>
              <Box sx={messageSx}>Visit day</Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MillosLoop;
